import React, { useState, useEffect } from 'react';
import './App.css';

// ログイン画面
function LoginPage({ onLogin }) {
  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, password })
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('auth_token', data.token);
        onLogin();
      } else {
        setError(data.error || 'ログインに失敗しました');
      }
    } catch {
      setError('サーバーに接続できません');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <h1>広告ガイドラインチェッカー 2026</h1>
        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-field">
            <label>ID</label>
            <input
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="IDを入力"
              required
              autoFocus
            />
          </div>
          <div className="login-field">
            <label>パスワード</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="パスワードを入力"
              required
            />
          </div>
          {error && <p className="login-error">{error}</p>}
          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? 'ログイン中...' : 'ログイン'}
          </button>
        </form>
      </div>
    </div>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [guidelines, setGuidelines] = useState([]);
  const [selectedGuidelines, setSelectedGuidelines] = useState(() => {
    // localStorageから選択状態を復元
    const saved = localStorage.getItem('selectedGuidelines');
    return saved ? JSON.parse(saved) : [];
  });
  const [expandedGuideline, setExpandedGuideline] = useState(null);
  const [guidelineTexts, setGuidelineTexts] = useState({});
  const [pdfFiles, setPdfFiles] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [checkTab, setCheckTab] = useState('image'); // 'image' | 'text'
  const [textInput, setTextInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

// 起動時にトークン確認
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetch('/api/verify-token', {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(res => {
        setIsLoggedIn(res.ok);
        setAuthChecked(true);
      }).catch(() => setAuthChecked(true));
    } else {
      setAuthChecked(true);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    setIsLoggedIn(false);
  };

  // ガイドライン一覧取得
  useEffect(() => {
    if (isLoggedIn) fetchGuidelines();
  }, [isLoggedIn]);

  // 選択状態が変更されたときにlocalStorageに保存
  useEffect(() => {
    localStorage.setItem('selectedGuidelines', JSON.stringify(selectedGuidelines));
  }, [selectedGuidelines]);

  const fetchGuidelines = async () => {
    try {
      const response = await fetch('/api/guidelines');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const fetchedGuidelines = data.guidelines || [];
      setGuidelines(fetchedGuidelines);
      
      // 存在するガイドラインのみ選択状態を維持
      const guidelineFilenames = fetchedGuidelines.map(g => g.filename);
      setSelectedGuidelines(prev => {
        const filtered = prev.filter(filename => guidelineFilenames.includes(filename));
        // 変更があればlocalStorageを更新
        if (filtered.length !== prev.length) {
          localStorage.setItem('selectedGuidelines', JSON.stringify(filtered));
        }
        return filtered;
      });
    } catch (error) {
      console.error('ガイドライン取得エラー:', error);
      setGuidelines([]); // エラー時は空の配列を設定
    }
  };

  // ガイドラインアップロード（複数対応）
  const handlePdfUpload = async (e) => {
    e.preventDefault();
    if (pdfFiles.length === 0) return;

    setLoading(true);
    setUploadStatus(`${pdfFiles.length}件のファイルをアップロード中...`);

    const formData = new FormData();
    pdfFiles.forEach(file => formData.append('pdf', file));

    try {
      const response = await fetch('/api/upload-pdf', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const errMsg = data.errors && data.errors.length > 0
          ? `（${data.errors.length}件エラー）`
          : '';
        setUploadStatus(`${data.files.length}件のファイルを解析しました${errMsg}`);
        setPdfFiles([]);
        fetchGuidelines();
      } else {
        setUploadStatus('エラー: ' + (data.details || data.error));
      }
    } catch (error) {
      console.error('アップロードエラー:', error);
      setUploadStatus('アップロードに失敗しました: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // すべてをチェック/解除
  const handleToggleAll = () => {
    if (selectedGuidelines.length === guidelines.length) {
      // すべて選択されている場合は解除
      setSelectedGuidelines([]);
    } else {
      // すべて選択
      setSelectedGuidelines(guidelines.map(g => g.filename));
    }
  };

  // ガイドライン選択（複数選択対応）
  const handleGuidelineSelect = async (filename) => {
    // 選択状態をトグル
    setSelectedGuidelines(prev => {
      if (prev.includes(filename)) {
        return prev.filter(f => f !== filename);
      } else {
        return [...prev, filename];
      }
    });
  };

  // テキスト表示のトグル
  const handleTextToggle = async (filename) => {
    // 詳細表示のトグル
    if (expandedGuideline === filename) {
      setExpandedGuideline(null);
    } else {
      setExpandedGuideline(filename);
      // テキストがまだ取得されていない場合は取得
      if (!guidelineTexts[filename]) {
        setLoading(true);
        try {
          const response = await fetch(`/api/guideline/${filename}`);
          const data = await response.json();
          setGuidelineTexts(prev => ({
            ...prev,
            [filename]: data.text
          }));
        } catch (error) {
          console.error('ガイドラインテキスト取得エラー:', error);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  // ガイドライン削除
  const handleGuidelineDelete = async (filename) => {
    if (!window.confirm(`${filename} を削除してもよろしいですか？`)) {
      return;
    }

    try {
      const response = await fetch(`/api/guideline/${filename}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setGuidelines(prev => prev.filter(g => g.filename !== filename));
        setSelectedGuidelines(prev => {
          const newSelected = prev.filter(f => f !== filename);
          // localStorageも更新
          localStorage.setItem('selectedGuidelines', JSON.stringify(newSelected));
          return newSelected;
        });
        setGuidelineTexts(prev => {
          const newTexts = { ...prev };
          delete newTexts[filename];
          return newTexts;
        });
        if (expandedGuideline === filename) {
          setExpandedGuideline(null);
        }
      } else {
        alert('削除に失敗しました');
      }
    } catch (error) {
      console.error('ガイドライン削除エラー:', error);
      alert('削除に失敗しました');
    }
  };

  // 未取得のガイドラインテキストを一括フェッチ
  const ensureGuidelineTexts = async (filenames) => {
    const missing = filenames.filter(f => !guidelineTexts[f]);
    if (missing.length === 0) return;

    const fetched = {};
    await Promise.all(missing.map(async (filename) => {
      try {
        const res = await fetch(`/api/guideline/${filename}`);
        const data = await res.json();
        fetched[filename] = data.text || '';
      } catch {
        fetched[filename] = '';
      }
    }));
    setGuidelineTexts(prev => ({ ...prev, ...fetched }));
    return fetched;
  };

  // テキスト適合性判定
  const handleTextComplianceCheck = async (e) => {
    e.preventDefault();
    if (!textInput.trim() || selectedGuidelines.length === 0) {
      alert('テキストと少なくとも1つのガイドラインを選択してください');
      return;
    }

    setLoading(true);
    setResult(null);

    // 未取得のガイドラインを先にフェッチ
    const freshTexts = await ensureGuidelineTexts(selectedGuidelines);
    const allTexts = { ...guidelineTexts, ...freshTexts };

    const combinedGuidelineText = selectedGuidelines
      .map(filename => `=== ${filename} ===\n${allTexts[filename] || ''}`)
      .join('\n\n');

    try {
      const response = await fetch('/api/check-text-compliance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: textInput,
          guidelineText: combinedGuidelineText,
          selectedGuidelines
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResult(data.success ? data : { error: data.error || '判定に失敗しました' });
    } catch (error) {
      setResult({ error: `エラー: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  // 画像適合性判定（複数ガイドライン対応）
  const handleComplianceCheck = async (e) => {
    e.preventDefault();
    if (!imageFile || selectedGuidelines.length === 0) {
      alert('画像と少なくとも1つのガイドラインを選択してください');
      return;
    }

    setLoading(true);
    setResult(null);

    // 未取得のガイドラインを先にフェッチ
    const freshTexts = await ensureGuidelineTexts(selectedGuidelines);
    const allTexts = { ...guidelineTexts, ...freshTexts };

    // 選択されたガイドラインのテキストを結合
    const combinedGuidelineText = selectedGuidelines
      .map(filename => `=== ${filename} ===\n${allTexts[filename] || ''}`)
      .join('\n\n');

    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('guidelineText', combinedGuidelineText);
    formData.append('selectedGuidelines', JSON.stringify(selectedGuidelines));

    try {
      const response = await fetch('/api/check-compliance', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('サーバーエラー応答:', errorData);
        throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setResult(data);
      } else {
        console.error('サーバーからのエラー:', data);
        setResult({ 
          error: data.error || '判定に失敗しました',
          details: data.details,
          type: data.type
        });
      }
    } catch (error) {
      console.error('適合性判定エラー:', error);
      console.error('エラーメッセージ:', error.message);
      console.error('エラータイプ:', error.constructor.name);
      
      // より詳細なエラー情報を表示
      let errorMessage = '適合性判定に失敗しました';
      
      if (error.message.includes('Failed to fetch')) {
        errorMessage = 'サーバーに接続できません。サーバーが起動しているか確認してください。';
      } else if (error.message.includes('Content-Type')) {
        errorMessage = 'サーバーからの応答形式に問題があります。';
      } else if (error.message.includes('Network')) {
        errorMessage = 'ネットワークエラーが発生しました。接続を確認してください。';
      } else if (error.message) {
        errorMessage = `エラー: ${error.message}`;
      }
      
      setResult({ 
        error: errorMessage,
        details: error.message,
        type: error.constructor.name
      });
    } finally {
      setLoading(false);
    }
  };

  const getJudgmentColor = (judgment) => {
    switch (judgment) {
      case '適合': return '#4CAF50';
      case '要確認': return '#FF9800';
      case '修正推奨': return '#F44336';
      case '抵触': return '#D32F2F';
      default: return '#666';
    }
  };

  if (!authChecked) return null;
  if (!isLoggedIn) return <LoginPage onLogin={() => setIsLoggedIn(true)} />;

  return (
    <div className="App">
      <header className="App-header">
        <h1>広告ガイドラインチェッカー 2026</h1>
        <p>PDFガイドラインをアップロードし、画像の適合性をAIで判定</p>
        <p className="disclaimer">AIによる判定の為、最終決定は人間が行ってください。</p>
        <button className="logout-btn" onClick={handleLogout}>ログアウト</button>
      </header>

      <main className="container">
        {/* ガイドラインアップロードセクション */}
        <section className="section">
          <h2>1. ガイドラインアップロード</h2>
          <p className="upload-hint">対応形式: PDF / TXT / CSV / Excel (.xlsx, .xls)　複数ファイル同時アップロード可</p>
          <form onSubmit={handlePdfUpload} className="upload-form">
            <div className="file-input">
              <input
                type="file"
                accept=".pdf,.txt,.csv,.xlsx,.xls"
                multiple
                onChange={(e) => setPdfFiles(Array.from(e.target.files))}
                required
              />
            </div>
            {pdfFiles.length > 0 && (
              <ul className="selected-files">
                {pdfFiles.map((f, i) => (
                  <li key={i}>{f.name}</li>
                ))}
              </ul>
            )}
            <button type="submit" disabled={loading || pdfFiles.length === 0}>
              {loading ? '処理中...' : `アップロードして解析${pdfFiles.length > 1 ? ` (${pdfFiles.length}件)` : ''}`}
            </button>
          </form>
          {uploadStatus && <p className="status">{uploadStatus}</p>}
        </section>

        {/* ガイドライン選択セクション */}
        <section className="section">
          <h2>2. ガイドライン選択</h2>
          {guidelines && guidelines.length > 0 && (
            <div className="guideline-controls">
              <button 
                className="toggle-all-btn"
                onClick={handleToggleAll}
                disabled={loading}
              >
                {selectedGuidelines.length === guidelines.length ? 'すべてを解除' : 'すべてをチェック'}
              </button>
              <span className="selection-count">
                {selectedGuidelines.length} / {guidelines.length} 件選択中
              </span>
            </div>
          )}
          <div className="guideline-list">
            {guidelines && guidelines.length > 0 ? (
              guidelines.map((guideline, index) => (
                <div
                  key={index}
                  className={`guideline-item ${selectedGuidelines.includes(guideline.filename) ? 'selected' : ''} ${expandedGuideline === guideline.filename ? 'expanded' : ''}`}
                >
                  <div className="guideline-header">
                    <div className="guideline-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedGuidelines.includes(guideline.filename)}
                        onChange={() => handleGuidelineSelect(guideline.filename)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="guideline-info">
                      <h3>{guideline.filename}</h3>
                      <p>サイズ: {guideline.size} bytes</p>
                      <p>作成日: {new Date(guideline.createdAt).toLocaleString('ja-JP')}</p>
                    </div>
                    <div className="guideline-actions">
                      <button 
                        className="text-toggle-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTextToggle(guideline.filename);
                        }}
                        title="テキスト確認"
                      >
                        {expandedGuideline === guideline.filename ? '閉じる' : 'テキスト確認'}
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGuidelineDelete(guideline.filename);
                        }}
                        title="削除"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  {expandedGuideline === guideline.filename && (
                    <div className="guideline-content">
                      <h4>ガイドライン内容：</h4>
                      <div className="guideline-text">
                        {guidelineTexts[guideline.filename] || '読み込み中...'}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p>ガイドラインがありません。PDFをアップロードしてください。</p>
            )}
          </div>
          {selectedGuidelines.length > 0 && (
            <div className="selected-info">
              <p>選択中のガイドライン: {selectedGuidelines.length}件</p>
            </div>
          )}
        </section>

        {/* 適合性判定セクション */}
        <section className="section">
          <h2>3. 適合性判定</h2>
          {selectedGuidelines.length > 0 && (
            <div className="guideline-preview">
              <h3>選択中のガイドライン：</h3>
              <ul>
                {selectedGuidelines.map(filename => (
                  <li key={filename}>{filename}</li>
                ))}
              </ul>
            </div>
          )}

          {/* タブ切り替え */}
          <div className="check-tabs">
            <button
              className={`check-tab ${checkTab === 'image' ? 'active' : ''}`}
              onClick={() => { setCheckTab('image'); setResult(null); }}
            >
              画像判定
            </button>
            <button
              className={`check-tab ${checkTab === 'text' ? 'active' : ''}`}
              onClick={() => { setCheckTab('text'); setResult(null); }}
            >
              テキスト判定
            </button>
          </div>

          {/* 画像判定フォーム */}
          {checkTab === 'image' && (
            <form onSubmit={handleComplianceCheck} className="upload-form">
              <div className="file-input">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file && file.size > 2 * 1024 * 1024) {
                      alert(`画像ファイルは2MB以内にしてください。\n選択したファイル: ${(file.size / 1024 / 1024).toFixed(1)}MB`);
                      e.target.value = '';
                      setImageFile(null);
                      return;
                    }
                    setImageFile(file || null);
                  }}
                  required
                />
                {imageFile && <span className="file-name">{imageFile.name}（{(imageFile.size / 1024 / 1024).toFixed(1)}MB）</span>}
              </div>
              <p className="upload-hint">最大2MB（JPG / PNG / GIF など）</p>
              <button
                type="submit"
                disabled={loading || !imageFile || selectedGuidelines.length === 0}
              >
                {loading ? '判定中...' : '適合性を判定'}
              </button>
            </form>
          )}

          {/* テキスト判定フォーム */}
          {checkTab === 'text' && (
            <form onSubmit={handleTextComplianceCheck} className="upload-form">
              <div className="text-check-input">
                <label htmlFor="textInput">判定するテキストを入力（キャッチフレーズ・広告文など）</label>
                <textarea
                  id="textInput"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="例：今だけ！驚異の100%当選！誰でも必ず儲かる..."
                  maxLength={300}
                  rows={5}
                  required
                />
                <span className="char-count">{textInput.length} / 300文字</span>
              </div>
              <button
                type="submit"
                disabled={loading || !textInput.trim() || selectedGuidelines.length === 0}
              >
                {loading ? '判定中...' : 'テキストを判定'}
              </button>
            </form>
          )}
        </section>

        {/* 結果表示セクション */}
        {result && (
          <section className="section" id="print-result">
            <div className="result-header">
              <h2>4. 判定結果</h2>
              {!result.error && (
                <button className="download-pdf-btn" onClick={() => window.print()}>
                  PDFとして保存
                </button>
              )}
            </div>
            {result.error ? (
              <div className="error">
                <p>エラー: {result.error}</p>
              </div>
            ) : (
              <div className="result">
                <div className="result-left">
                  <div className="judgment" style={{ color: getJudgmentColor(result.judgment) }}>
                    <h3>判定: {result.judgment}</h3>
                  </div>

                  {/* 画像判定の場合：画像プレビュー */}
                  {result.imageFile && (
                    <div className="image-details">
                      <h4>判定対象画像:</h4>
                      <div className="image-preview-container">
                        <div className="image-preview">
                          <img
                            src={`/uploads/${encodeURIComponent(result.imageFile)}`}
                            alt={result.originalImageName}
                            className="preview-img"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* テキスト判定の場合：入力テキスト表示 */}
                  {result.checkedText && (
                    <div className="image-details">
                      <h4>判定対象テキスト:</h4>
                      <div className="checked-text-box">{result.checkedText}</div>
                    </div>
                  )}
                </div>
                
                <div className="result-right">
                  <div className="reason">
                    <h4>判定理由:</h4>
                    <div className="reason-content">
                      {result.reason.split(/【(.*?)】/).map((part, index) => {
                        const trimmedPart = part.trim();
                        
                        // 空の部分はスキップ
                        if (!trimmedPart) return null;
                        
                        // セクションヘッダーの場合
                        if (index % 2 === 1 && trimmedPart) {
                          return (
                            <div key={index} className="reason-section-header">
                              <strong>【{trimmedPart}】</strong>
                            </div>
                          );
                        }
                        
                        // セクション内容の場合
                        if (index % 2 === 0 && trimmedPart) {
                          const lines = trimmedPart.split('\n').filter(line => line.trim());
                          
                          return (
                            <div key={index} className="reason-section-content">
                              {lines.map((line, lineIndex) => {
                                const trimmedLine = line.trim();
                                if (!trimmedLine) return null;
                                
                                // 引用符で囲まれた部分（抵触箇所）を太字に
                                if (trimmedLine.includes('「') || trimmedLine.includes('『')) {
                                  const boldText = trimmedLine.replace(/([「『])(.*?)([」』])/g, '<strong>$1$2$3</strong>');
                                  return (
                                    <p key={lineIndex} className="reason-paragraph" dangerouslySetInnerHTML={{ __html: boldText }} />
                                  );
                                }
                                
                                // 条項番号を含む行
                                if (trimmedLine.match(/第\d+条|条項\d+/)) {
                                  return (
                                    <p key={lineIndex} className="reason-paragraph guideline-reference">
                                      <strong>{trimmedLine}</strong>
                                    </p>
                                  );
                                }
                                
                                // 通常の文章
                                return (
                                  <p key={lineIndex} className="reason-paragraph">
                                    {trimmedLine}
                                  </p>
                                );
                              })}
                            </div>
                          );
                        }
                        
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}
      </main>
      
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>処理中...</p>
        </div>
      )}
    </div>
  );
}

export default App;
